import axios from "axios";
import { api } from "./client";
import { API_BASE_URL } from "../lib/apiBase";

// Локалният резерв („edge", docs/edge.md): облачните endpoint-и минават през
// общата `api` инстанция (Bearer, режим-зависим адрес); заявките към
// САМАТА КАСА са със суров axios — те носят САМО `X-Edge-Ticket`, никога
// облачния JWT, и не бива да минават през 401/403 интерсепторите.
// Формите са огледало на договора OFFLINE-EDGE-SPEC (§2, §5, §6, §7).

/** Таймаут на сондите към КАСАТА (§8: 3 s) — тя е в същата мрежа. */
export const EDGE_PROBE_TIMEOUT_MS = 3_000;

/**
 * Таймаут на сондата към ОБЛАКА — по-къс от този към касата нарочно.
 * Сондата не носи работа: тя е въпрос „жив ли си", зададен на всеки 2 s,
 * докато нещо не е наред. Отговор, който закъснява повече от самия интервал,
 * би трупал сонди една върху друга и би бавил решението точно когато то
 * трябва да е бързо. Истинските заявки си имат бюджет по маршрут
 * (`api/timeouts.ts`) — там се чака дълго, тук не.
 */
export const CLOUD_PROBE_TIMEOUT_MS = 2_000;

/**
 * Над колко време без вест от касата облакът я смята за мълчаща.
 * ОГЛЕДАЛО на `MASTER_PRESENCE_STALE_SECONDS` в BE
 * (`app/features/configuration/edge_service.py`) = 2 × heartbeat + 30 s.
 * Тук е само за ТЕКСТА („последно видяна в HH:MM") — решението кой режим е в
 * сила си остава на BE-то; по-нисък праг тук значи само излишно предупреждение.
 */
export const EDGE_PRESENCE_STALE_MS = 150_000;

/** `GET /configuration/edge` (§7). */
export interface EdgeConfigurationOut {
  enabled: boolean;
  ip: string | null;
  port: number;
  master_client_id: string | null;
  mode: "cloud" | "local";
  edge_api: number;
  /**
   * ГОТОВНОСТТА на обекта, както облакът я вижда през presence-а на касата.
   * Всички са по избор и `null`/липсващо значи „не знаем" (по-стар BE или каса
   * от по-ранен билд) — екранът мълчи за тях, вместо да твърди нула.
   *
   * `clients_seen` е броят РАЗЛИЧНИ устройства, стигали до `/edge/*` на касата
   * през последните 24 h: включеният резерв се вижда по касата, но дали
   * терминалите и мониторите ще проработят без интернет — само по това число.
   */
  clients_seen?: number | null;
  /** Събития в дневника на касата, които тя още не е качила (качват се сами). */
  pending_events?: number | null;
  /** Събития, които облакът е отказал и чакат човек при касата (§17.1). */
  unresolved_events?: number | null;
  /** Кога облакът последно е чул касата (ISO, часовникът на BE-то). */
  last_seen_at?: string | null;
}

/** `PUT /configuration/edge` (право `manage_devices`). */
export interface EdgeConfigurationUpdateIn {
  enabled: boolean;
  force_cloud: boolean;
}

/** `POST /user/edge-ticket` (§2). */
export interface EdgeTicketOut {
  ticket: string;
  expires_at: string;
}

/** `GET {edge}/health` — без auth (§5). */
export interface EdgeHealthOut {
  ok: boolean;
  edge_api: number;
  app_version: string;
  enabled: boolean;
  client_id: string;
  /**
   * Какво касата умее ОТВЪД договора от §5. `reservations: 1` — сервира
   * „Резервации" и „Настани" без интернет (облакът е пуснал
   * `offline_reservations` в snapshot-а). Липсва при по-стара каса → нищо.
   */
  capabilities?: { reservations?: number };
}

/** `GET {edge}/status` — с билет (§6). */
export interface EdgeStatusOut {
  enabled: boolean;
  mode: "cloud" | "local";
  local_since: string | null;
  cloud_reachable: boolean;
  mqtt_connected: boolean;
  pending_events: number;
  /**
   * БРОЯТ на действията, които облакът е ОТКАЗАЛ и които чакат ЧОВЕК на
   * касата (`failed_permanent`, `conflict`, прескочените заради тях) — §17.1
   * на договора. По-стара каса не го праща → чете се като 0.
   *
   * КАПАН: тук полето `unresolved` е СПИСЪКЪТ с кратките описания за портала
   * на касата, а не брой — в `/sync-state` същото име е броят. FE-то ползва
   * само броя и затова чете `unresolved_events` оттук.
   */
  unresolved_events?: number;
  synced: boolean;
  last_replay: {
    at: string | null;
    applied: number;
    conflicts: number;
    errors: number;
  };
  snapshot: { generated_at: string | null; users: number };
  mirror: Record<string, string>;
}

// Списъкът на устройствата, стигали до касата, НЕ се описва тук: касата го
// сервира само на своя портал (`GET /api/edge/status`), а LAN `/edge/status` е
// по договора и не изнася кой друг е на касата. FE-то вижда само БРОЯ им, през
// `clients_seen` в `GET /configuration/edge`.

/** `POST {edge}/mode/enter` (§5) — идемпотентно. */
export interface EdgeModeEnterOut {
  mode: "local";
  local_since: string;
}

export const getEdgeConfiguration = async (): Promise<EdgeConfigurationOut> => {
  const response = await api.get<EdgeConfigurationOut>("/configuration/edge");
  return response.data;
};

export const updateEdgeConfiguration = async (
  data: EdgeConfigurationUpdateIn,
): Promise<EdgeConfigurationOut> => {
  const response = await api.put<EdgeConfigurationOut>(
    "/configuration/edge",
    data,
  );
  return response.data;
};

export const issueEdgeTicket = async (): Promise<EdgeTicketOut> => {
  const response = await api.post<EdgeTicketOut>("/user/edge-ticket");
  return response.data;
};

/**
 * Присъдата за облака — ЧЕТИРИЗНАЧНА, защото трите различни повреди искат
 * три различни реакции:
 *
 *  • `up`        облакът работи: 2xx И тяло `{ "status": "ok" }`;
 *  • `app_down`  отговор ИМА, но не е нашият облак: 5xx, тяло `"degraded"`
 *                (BE-то е живо, базата му не е — `app/main.py::health`), или
 *                тяло, което изобщо не е очакваното JSON. Последното е
 *                хотелски/операторски прихващащ прокси: той връща 200 и HTML
 *                на СВОЯ вход, а дотук всеки 2xx минаваше за жив облак и
 *                терминалът оставаше залостен срещу нищо;
 *  • `down`      няма отговор: таймаут, DNS, отказана връзка;
 *  • `unknown`   не е сондирано или няма адрес на облака.
 *
 * Разликата между `app_down` и `down` е носеща: `app_down` при жива мрежа
 * най-често е НАШ деплой и обектът чака 60 s вместо да се замрази
 * (`lib/edge/edgeMode.ts`).
 */
export type CloudVerdict = "up" | "app_down" | "down" | "unknown";

/** Присъдата по ОТГОВОРА, не по статуса сам по себе си. Изнесена отделно,
 * за да може да се проверява без мрежа. */
export function verdictFromHealthResponse(
  status: number,
  body: unknown,
): CloudVerdict {
  if (status < 200 || status >= 300) return "app_down";
  if (!body || typeof body !== "object") return "app_down";
  const reported = (body as { status?: unknown }).status;
  return reported === "ok" ? "up" : "app_down";
}

/**
 * Сонда към облака (§8): суров axios към `{cloud}/health`, без auth, 2 s.
 * Въпросът не е „отговаря ли нещо на този адрес" — на него всяка кабелна
 * телевизия отговаря „да" — а „работи ли ОБЛАКЪТ".
 */
export const probeCloudVerdict = async (): Promise<CloudVerdict> => {
  try {
    const response = await axios.get(`${API_BASE_URL}/health`, {
      timeout: CLOUD_PROBE_TIMEOUT_MS,
      validateStatus: () => true,
    });
    return verdictFromHealthResponse(response.status, response.data);
  } catch {
    return "down";
  }
};

export const getEdgeHealth = async (
  edgeBaseUrl: string,
): Promise<EdgeHealthOut> => {
  const response = await axios.get<EdgeHealthOut>(`${edgeBaseUrl}/health`, {
    timeout: EDGE_PROBE_TIMEOUT_MS,
  });
  return response.data;
};

/**
 * `GET {edge}/status` (§6) — ПЪЛНИЯТ статус, зад билет: носи адреса на облака,
 * обобщение на snapshot-а и огледалните пътища.
 */
export const getEdgeStatus = async (
  edgeBaseUrl: string,
  ticket: string | null,
): Promise<EdgeStatusOut> => {
  const response = await axios.get<EdgeStatusOut>(`${edgeBaseUrl}/status`, {
    headers: ticket ? { "X-Edge-Ticket": ticket } : undefined,
    timeout: EDGE_PROBE_TIMEOUT_MS,
  });
  return response.data;
};

/** `GET {edge}/sync-state` — полетата, достъпни и без билет (§15.5, §17.1). */
export interface EdgeSyncStateOut {
  mode: "cloud" | "local";
  /** Събития, които касата още не е изпратила — качват се сами. */
  pending_events: number;
  /**
   * Действия, които облакът е отказал и които касата НЕ може да качи сама:
   * чакат човек при нея („Опитай пак" / „Отпиши" в портала ѝ). Отделно от
   * `pending_events`, защото значи различно нещо (§17.1 на договора), и
   * липсва при по-стара каса → чете се като 0.
   */
  unresolved?: number;
  synced: boolean;
  /**
   * КАКВО ВИЖДА КАСАТА за облака — вторият, НЕЗАВИСИМ свидетел. Терминалът
   * знае само какво се случва с неговия Wi-Fi; касата е на друга мрежова
   * карта и сондира облака сама. Липсва при по-стара каса → чете се като
   * `"unknown"`, НЕ като грешка.
   */
  cloud_seen?: CloudVerdict;
  /** На колко милисекунди е присъдата. По-стара от `WITNESS_MAX_AGE_MS`
   * (`lib/edge/edgeCloudWitness.ts`) не е свидетелство, а спомен. */
  cloud_seen_age_ms?: number | null;
  /** Държи ли касата MQTT връзка към облака — диагностично, не решава. */
  mqtt_connected?: boolean;
  /**
   * Касата е в локален режим по РЪЧНО решение на човек (§19.5, FiscalAPI
   * 1.96.1) и НЕ се връща сама, колкото и добре да вижда облака — изходът е
   * `POST {edge}/mode/leave`. Липсва при по-стара каса → `false`.
   */
  manual_override?: boolean;
  /**
   * …и решението е натиснато от ТОВА устройство (касата го познава по
   * браузъра и адреса). Само то го отменя само, щом отново види облака —
   * терминал, който е имал интернет през цялото време, не бива.
   */
  manual_override_by_this_device?: boolean;
  /**
   * Кога касата е получила последния изглед на резервациите (ISO на касата).
   * `null`/липсва — каса без резервации или още без изглед.
   */
  reservations_as_of?: string | null;
  /** На колко милисекунди е този изглед — `lib/edge/edgeReservationsFeed.ts`
   * го пита, за да не дърпа пак, ако друг терминал току-що го е огледал. */
  reservations_view_age_ms?: number | null;
}

/** `POST {edge}/mode/leave` (§19.1) — отменя ръчното решение; идемпотентно. */
export interface EdgeModeLeaveOut {
  /** Режимът СЛЕД отмяната: `cloud`, ако касата е успяла да излезе веднага. */
  mode: "cloud" | "local";
  local_since: string | null;
  manual_override: boolean;
  /** Имало ли е решение за махане (втори натиск → `false`). */
  cleared: boolean;
}

/**
 * Отмяната пуска replay-а на касата веднага (качва дневника и излиза), затова
 * бюджетът е над този на сондите. Изтече ли, нищо не е загубено: флагът се
 * маха ПРЕДИ replay-а, а следващата сонда вижда истинския режим.
 */
export const EDGE_MODE_LEAVE_TIMEOUT_MS = 10_000;

/**
 * Изпратила ли е касата дневника си — въпросът, от който зависи връщането в
 * облака (§15.5). Отделен от `getEdgeStatus`, защото точно терминалът БЕЗ
 * билет (изгасен през нощта, изтекъл билет) трябва да може да го зададе, а
 * пълният статус остава затворен за локалната мрежа. Билетът се праща, когато
 * го има — касата не го изисква тук.
 */
export const getEdgeSyncState = async (
  edgeBaseUrl: string,
  ticket: string | null,
): Promise<EdgeSyncStateOut> => {
  const response = await axios.get<EdgeSyncStateOut>(
    `${edgeBaseUrl}/sync-state`,
    {
      headers: ticket ? { "X-Edge-Ticket": ticket } : undefined,
      timeout: EDGE_PROBE_TIMEOUT_MS,
    },
  );
  return response.data;
};

/**
 * ЗАЩО се иска замразяване — касата решава дали да го изпълни (§5):
 *
 *  • `probe`            терминалът е сондирал и облакът мълчи. Минава през
 *                       ВЕТОТО на касата: види ли тя облака жив в момента,
 *                       отказва с 409, защото проблемът е само в това
 *                       устройство, а `mode/enter` замразява ЦЕЛИЯ обект;
 *  • `cloud_fence`      самият облак е върнал 423 („обектът е в локален
 *                       режим") — няма какво да се проверява, байпас;
 *  • `manual_override`  човек при касата е решил. Байпас, логва се шумно.
 */
export type EdgeModeEnterReason = "probe" | "cloud_fence" | "manual_override";

/** Кодът, с който касата отказва замразяване, защото тя вижда облака ЖИВ. */
export const EDGE_ENTER_REFUSED_CODE = "cloud_is_up";

/**
 * Разпознава точно този отказ (409 + `code: "cloud_is_up"`). Всичко друго е
 * обикновен провал: отказът значи „само това устройство е без интернет" и се
 * показва на човека по друг начин, а `mode/enter` НЕ се повтаря в цикъл.
 */
export function isCloudIsUpRefusal(error: unknown): boolean {
  if (!axios.isAxiosError(error)) return false;
  if (error.response?.status !== 409) return false;
  const data = error.response.data as { code?: unknown } | undefined;
  // Кодът е договорът; по-стара каса без него, но с 409 на този endpoint
  // също казва „не" — и повтарянето пак няма смисъл.
  return data?.code === undefined || data.code === EDGE_ENTER_REFUSED_CODE;
}

export const enterEdgeLocalMode = async (
  edgeBaseUrl: string,
  ticket: string,
  reason: EdgeModeEnterReason = "probe",
): Promise<EdgeModeEnterOut> => {
  const response = await axios.post<EdgeModeEnterOut>(
    `${edgeBaseUrl}/mode/enter`,
    { reason },
    {
      headers: { "X-Edge-Ticket": ticket },
      timeout: EDGE_PROBE_TIMEOUT_MS,
    },
  );
  return response.data;
};

/**
 * `POST {edge}/mode/leave` (§19.1, §19.5) — отменя РЪЧНОТО задържане в локален
 * режим. Иска билет, но не право (касата го проверява само за самоличност);
 * правото на бутона се пази в `edgeMode.ts`, както при ръчното влизане.
 */
export const leaveEdgeLocalMode = async (
  edgeBaseUrl: string,
  ticket: string,
): Promise<EdgeModeLeaveOut> => {
  const response = await axios.post<EdgeModeLeaveOut>(
    `${edgeBaseUrl}/mode/leave`,
    {},
    {
      headers: { "X-Edge-Ticket": ticket },
      timeout: EDGE_MODE_LEAVE_TIMEOUT_MS,
    },
  );
  return response.data;
};

/**
 * `GET /sync/bill-alias/{local}` — облачният номер на сметка, създадена БЕЗ
 * интернет (§16.3). Терминалът я държи отворена с локалния ѝ номер и при
 * връщането в облака трябва да разбере под кой номер е горе, иначе екранът
 * остава без избрана сметка (списъкът вече носи облачната).
 *
 * `null` = няма такъв псевдоним: номерът е облачен или дневникът за него още
 * не е стигнал до облака.
 */
export const resolveCloudBillId = async (
  localBillId: number,
): Promise<number | null> => {
  const response = await api.get<{ cloud_bill_id: number | null }>(
    `/sync/bill-alias/${localBillId}`,
    { timeout: EDGE_PROBE_TIMEOUT_MS },
  );
  return response.data.cloud_bill_id ?? null;
};
